import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
    Paper,
    IconButton,
    Tooltip,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import RestoreIcon from '@mui/icons-material/Restore';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Configure PDF.js worker.
pdfjs.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + '/pdf.worker.min.mjs';

export default function DocumentViewer({
    document,
    onClose,
    onValidate,
    onIncorrect,
    onDelete,
    onNext,
    onDocumentUpdated, // Callback for split/unsplit updates.
}) {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [splitDialogOpen, setSplitDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Build the download URL for the protected endpoint.
    const downloadUrl = `/api/TrainingSetDocuments/${document.trainingSetDocumentId}/download`;

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const onDocumentLoadError = (error) => {
        console.error('Error loading PDF:', error);
    };

    // Use lowercase property names as returned by the backend.
    // For a split document, use its startPage and endPage; otherwise, show all pages.
    const effectiveStartPage = document.isSplit && document.startPage ? document.startPage : 1;
    const effectiveEndPage = document.isSplit && document.endPage ? document.endPage : numPages;

    const pagesToRender = [];
    if (numPages && effectiveEndPage) {
        const from = effectiveStartPage;
        const to = effectiveEndPage <= numPages ? effectiveEndPage : numPages;
        for (let i = from; i <= to; i++) {
            pagesToRender.push(i);
        }
    }

    const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

    const handleOpenSplitDialog = () => setSplitDialogOpen(true);
    const handleCloseSplitDialog = () => setSplitDialogOpen(false);

    // Allow splitting regardless of the current split state.
    const handleConfirmSplit = async () => {
        try {
            const payload = {
                ParentRecordId: document.trainingSetDocumentId,
                SplitPage: currentPage,
                TotalPages: numPages,
            };
            const response = await fetch('/api/TrainingSetDocuments/split', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });
            if (response.ok) {
                const updateResponse = await response.json();
                // Let the parent know about the split.
                if (onDocumentUpdated) onDocumentUpdated(updateResponse);
            } else {
                console.error('Split failed');
            }
        } catch (error) {
            console.error('Error splitting document:', error);
        } finally {
            setSplitDialogOpen(false);
        }
    };

    // Unsplit updates only the viewer.
    const handleUnsplit = async () => {
        try {
            const payload = { ParentRecordId: document.trainingSetDocumentId };
            const response = await fetch('/api/TrainingSetDocuments/unsplit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });
            if (response.ok) {
                const updatedDoc = await response.json();
                // Pass the updated unsplit document with the unsplitOnly flag.
                if (onDocumentUpdated) onDocumentUpdated(updatedDoc, { unsplitOnly: true });
            } else {
                console.error('Unsplit failed');
            }
        } catch (error) {
            console.error('Error unsplitting document:', error);
        }
    };


    // Delete dialog handlers.
    const handleOpenDeleteDialog = () => setDeleteDialogOpen(true);
    const handleCloseDeleteDialog = () => setDeleteDialogOpen(false);
    const handleConfirmDelete = async () => {
        try {
            await onDelete();
        } catch (error) {
            console.error('Error deleting document:', error);
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    return (
        <Paper sx={{ p: 2, position: 'relative', height: '80vh', overflow: 'hidden' }}>
            {/* Toolbar */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: '1px solid #ddd',
                }}
            >
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Always show split icon when currentPage >= 2 */}
                    {currentPage >= 2 && (
                        <Tooltip title="Split document before current page">
                            <IconButton onClick={handleOpenSplitDialog} size="small">
                                <ContentCutIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {/* Show unsplit icon when document is split */}
                    {document.isSplit && (
                        <Tooltip title="Unsplit document">
                            <IconButton onClick={handleUnsplit} size="small">
                                <RestoreIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Validate">
                        <IconButton onClick={onValidate} size="small">
                            <CheckCircleIcon color="success" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Incorrect">
                        <IconButton onClick={onIncorrect} size="small">
                            <CancelIcon color="error" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={handleOpenDeleteDialog} size="small">
                            <DeleteIcon color="error" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Next Document">
                        <IconButton onClick={onNext} size="small">
                            <NavigateNextIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* PDF Rendering Area */}
            <Box sx={{ overflowY: 'auto', height: 'calc(80vh - 60px)', p: 1 }}>
                <Document
                    file={downloadUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                >
                    {pagesToRender.map((pageNum) => (
                        <div key={`page_${pageNum}`} onClick={() => handlePageClick(pageNum)}>
                            <Page pageNumber={pageNum} width={600} renderTextLayer={true} renderAnnotationLayer={true} />
                        </div>
                    ))}
                </Document>
            </Box>

            {/* Split Confirmation Dialog */}
            <Dialog open={splitDialogOpen} onClose={handleCloseSplitDialog}>
                <DialogTitle>Confirm Split</DialogTitle>
                <DialogContent>
                    <Typography>Do you want to split the document before page {currentPage}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSplitDialog}>Cancel</Button>
                    <Button onClick={handleConfirmSplit} variant="contained" color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this document?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
